// Shared modal shown when the backend flags a likely-existing patient at
// registration time (task #24, 2026-07-23). The backend returns HTTP 409
// with error.code === 'likely_duplicate_patient' and a `candidates` payload;
// this modal renders those candidates and lets the caller either:
//   - Adopt one existing candidate (onUseExisting(candidate))
//   - Force-create a new record anyway (onForceCreate())
//
// Kept self-contained (no import from features/doctor/documents/Modal.jsx)
// because it's used from both the doctor walk-in flow AND the public
// /register wizard — coupling a public component to a doctor-scoped path
// would create a maintenance ambiguity.
//
// The modal is purely presentational — no data fetching, no mutations.
// Parent decides what "use existing" and "register anyway" actually do
// (navigate to chart / retry POST with force_create / redirect to /login).

export default function DuplicatePatientModal({
  candidates,
  onUseExisting,
  onForceCreate,
  onCancel,
  actionLabels = {
    useExisting: 'Use this patient',
    forceCreate: 'Register new anyway',
    cancel: 'Cancel',
  },
  contextMessage = null,
  submitting = false,
}) {
  if (!candidates || candidates.length === 0) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.() }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b-2 border-slate-100 flex items-start justify-between gap-3 sticky top-0 bg-white">
          <div>
            <h3 className="font-bold text-slate-900 text-lg leading-tight">
              Possible existing patient
            </h3>
            <div className="text-sm text-slate-500 mt-0.5">
              {contextMessage || (
                'Someone with matching details is already registered. Confirm ' +
                'with the patient before creating a new record.'
              )}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full w-8 h-8 flex items-center justify-center text-2xl leading-none transition"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-3">
          {candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onUseExisting?.(c)}
              disabled={submitting}
              className="w-full text-left border border-slate-200 hover:border-wantok-500 hover:bg-wantok-50 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg p-3 transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">
                    {c.name || 'Unnamed patient'}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    {c.date_of_birth && <span>DOB {c.date_of_birth}</span>}
                    {c.sex && <span className="capitalize">{c.sex}</span>}
                    {c.phone_number && <span>{c.phone_number}</span>}
                  </div>
                  <div className="text-[11px] text-wantok-700 mt-1.5 uppercase tracking-wider font-semibold">
                    Matched on {c.match_reason}
                  </div>
                </div>
                <div className="text-xs text-wantok-700 shrink-0 mt-1">
                  {actionLabels.useExisting} →
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="px-5 pb-5 pt-3 border-t-2 border-slate-100 flex flex-wrap justify-between gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-60 rounded-lg px-4 py-2 transition"
          >
            {actionLabels.cancel}
          </button>
          <button
            type="button"
            onClick={onForceCreate}
            disabled={submitting}
            className="text-sm font-semibold text-amber-800 hover:text-amber-900 hover:bg-amber-50 disabled:opacity-60 border border-amber-300 rounded-lg px-4 py-2 transition"
          >
            {submitting ? 'Working…' : actionLabels.forceCreate}
          </button>
        </div>
      </div>
    </div>
  )
}
